import axios from 'axios';

// TREATX-L1F-DNS-ACTIVATION (production correction) — same-origin
// browser transport. If `REACT_APP_BACKEND_URL` is empty/undefined at
// build time we emit `/api` so the browser targets the page's own
// hostname. This preserves the trusted `X-Forwarded-Host` the Emergent
// ingress forwards to the backend, which is what the L1-F surface
// resolver keys off. Production Publish must set the env to an empty
// string; local dev / preview still work because they set the env to
// the explicit backend origin.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// FIND-H-08 — canonical browser auth transport is the HttpOnly
// `treatx_auth` cookie. The axios instance is created with
// `withCredentials: true` so the browser automatically attaches the
// cookie on every same-site request. No manual Authorization header
// construction, no localStorage token read.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// FIND-H-08 — helper: read the readable `treatx_csrf` cookie set by the
// backend at login. The cookie is intentionally NOT HttpOnly so the
// double-submit CSRF pattern (Owner directive D2) can attach it to
// state-changing requests via the `X-CSRF-Token` header.
function readCsrfCookie(name = 'treatx_csrf') {
  try {
    const raw = typeof document !== 'undefined' ? (document.cookie || '') : '';
    const m = raw.split('; ').find((c) => c.startsWith(`${name}=`));
    return m ? decodeURIComponent(m.substring(`${name}=`.length)) : '';
  } catch {
    return '';
  }
}

// P0-CAMPAIGN-CREATION-CLOSURE-01 (§2) — STRING-SAFE API error extraction.
// FastAPI returns `detail` as an ARRAY OF OBJECTS for a 422 request-validation
// error (`[{type, loc, msg, input, url}]`). Passing that straight into a toast
// or into JSX renders an object as a React child, which throws
// "Objects are not valid as a React child" from the root <Toaster/> and
// UNMOUNTS THE WHOLE APP (white screen, unrecoverable by hash-only Back).
// Formatting only — no new error authority, no status interpretation.
export function apiErrorMessage(err, fallback = 'Operation failed') {
  const data = err?.response?.data;
  const pick = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v;
    if (Array.isArray(v)) {
      return v.map((x) => (typeof x === 'string' ? x : (x?.msg || x?.message || ''))).filter(Boolean).join('; ');
    }
    if (typeof v === 'object') return v.msg || v.message || v.detail || v.error || '';
    return String(v);
  };
  return (
    pick(data?.message) || pick(data?.detail) || pick(data?.error)
    || (typeof err?.message === 'string' ? err.message : '') || fallback
  );
}

// FINAL-UAT §7 — the provider application portal (Status Check + the
// onboarding wizard's document/submit calls) authenticates with an
// application-bound `status_session` whose double-submit partner is its OWN
// `treatx_status_csrf` cookie. Reusing the platform `treatx_csrf` here meant an
// unrelated unified-auth logout deleted the CSRF half of a still-valid portal
// session. Same pattern, same authority, distinct cookie.
const PORTAL_ENDPOINT_PREFIX = '/provider/applications';

const CSRF_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// P0-PROVIDER-ONBOARDING-RECOVERY-01 (Failure B) — ONE classification of the
// routes that are NOT RBAC-authenticated surfaces and own their own
// not-signed-in state:
//   - the provider onboarding wizard (BUG-4)
//   - the provider application Status Portal (`/clinic/application-status`),
//     whose visitor holds ONLY an application-bound `status_session`.
//
// Both mint the readable `treatx_csrf` cookie (it is shared by the unified
// auth authority AND the status-portal authority), which makes every
// "was this browser logged in?" heuristic read TRUE. Any background RBAC
// request on these routes 401s legitimately; converting that 401 into a
// global logout destroyed a valid portal session (POST /auth/logout deletes
// the SHARED `treatx_csrf` cookie) and bounced the provider to #/login.
//
// Exported so the RBAC context uses the SAME list instead of re-deriving it.
export const SELF_OWNED_SESSION_ROUTE_PREFIXES = [
  '#/provider/register',
  '#/provider/onboarding',
  '#/onboarding',
  '#/clinic/application-status',
];

export function isSelfOwnedSessionRoute(hash) {
  const h = typeof hash === 'string'
    ? hash
    : ((typeof window !== 'undefined' && typeof window.location?.hash === 'string') ? window.location.hash : '');
  return SELF_OWNED_SESSION_ROUTE_PREFIXES.some(
    (p) => h === p || h.startsWith(`${p}?`) || h.startsWith(`${p}/`),
  );
}

// FIND-H-08 — request interceptor. Attaches:
//   1. X-CSRF-Token header on state-changing requests (double-submit).
//   2. X-Soft-Launch-Session (retained — invite-key gate bypass; NOT auth).
//   3. X-Active-Workspace   (retained — messaging + legal SSOT hint).
// No Authorization header is emitted from browser code post-cutover.
api.interceptors.request.use((config) => {
  const url = config.url || '';
  const method = (config.method || 'get').toLowerCase();

  // TREATX-L1F: canonical viewer locale header. Backend surface
  // resolver consumes this as a POLICY input for the Turkish-Provider
  // language rule (md.8). NEVER used to infer residence.
  try {
    const loc = (typeof window !== 'undefined' && window.localStorage)
      ? (window.localStorage.getItem('i18nextLng') || window.localStorage.getItem('locale') || '')
      : '';
    if (loc && !config.headers['X-TreatX-Locale']) {
      config.headers['X-TreatX-Locale'] = String(loc).slice(0, 20);
    }
  } catch { /* noop */ }

  // Double-submit CSRF header for authenticated mutations.
  if (CSRF_METHODS.has(method)) {
    const portalCsrf = url.startsWith(PORTAL_ENDPOINT_PREFIX)
      ? readCsrfCookie('treatx_status_csrf')
      : '';
    // TECHNICAL-CLOSURE §1 — the platform cookie is a LEGACY-ONLY fallback for
    // a portal session minted before the cookie split (such a session has no
    // `treatx_status_csrf` cookie to send). It is not a client-side
    // authorisation decision: the server decides which cookie it accepts from
    // the stored session's own `csrf_scheme`, so for every session minted
    // after the split this fallback is refused with 403.
    const csrf = portalCsrf || readCsrfCookie();
    if (csrf) {
      config.headers['X-CSRF-Token'] = csrf;
    }
    // TREATX-CONTACT-CSRF-MULTIPART-01 — multipart bodies must keep the
    // browser-generated boundary. The instance default
    // `Content-Type: application/json` would otherwise be sent for a
    // FormData payload and the server could not parse it. Dropping the
    // header lets axios/the browser set
    // `multipart/form-data; boundary=…` itself.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
    }
  }

  // Preserve soft-launch invite-key session header — this is NOT auth,
  // it is a product-gate bypass token distinct from the authentication
  // cookie. Semantics identical to `SoftLaunchGate.jsx::installFetchInterceptor`:
  //   * only attach if a non-expired session exists in localStorage
  //   * never overwrite an explicit header set by the caller
  //   * silently no-op on localStorage / parse errors
  try {
    const sessTok = localStorage.getItem('treatx.softLaunch.sessionToken');
    const sessExp = localStorage.getItem('treatx.softLaunch.sessionExpiresAt');
    if (sessTok && sessExp && new Date(sessExp).getTime() > Date.now()) {
      if (!config.headers['X-Soft-Launch-Session']
          && !config.headers['x-soft-launch-session']) {
        config.headers['X-Soft-Launch-Session'] = sessTok;
      }
    }
  } catch { /* localStorage unavailable / non-browser — silent fallback */ }

  // R3-IMPL-001 + R6-IMPL-005: attach the active workspace header on
  // BOTH messaging routes (sender-authority SSOT) AND legal document
  // / signature routes (operational tenant SSOT). Backend remains the
  // sole authority — header is advisory; missing/invalid → JWT
  // fallback.
  // MTMR-P0 — messaging no longer consults this header for anything
  // (authorization AND sender identity now derive from the signed `act`
  // context in the auth cookie). Kept only for the legal/documents
  // surfaces that still read it as a hint.
  try {
    const needsActiveWorkspace =
      url.startsWith('/documents') ||
      url.startsWith('/signatures');
    if (needsActiveWorkspace) {
      const wsId = (typeof window !== 'undefined' &&
        window.localStorage?.getItem('treatx_last_active_workspace')) || '';
      if (wsId) {
        const role = wsId.includes(':') ? wsId.split(':').pop() : wsId;
        if (role && /^[a-z0-9_]+$/i.test(role) && role.length < 64) {
          config.headers['X-Active-Workspace'] = role;
        }
      }
    }
  } catch { /* localStorage unavailable / non-browser — silent fallback */ }
  return config;
});

// ── M14-AUDIT-REMEDIATION F2: Global 401 / 403 response handler ──
// Converts raw FastAPI auth errors into actionable UX. The interceptor
// is fire-and-forget — never throws; never re-redirects from /login.
//
//   401  →  session expired   →  toast + redirect to /login
//   403  →  role forbidden    →  NO global toast (COA-01 T5 fix below)
//
// COA-01 T5 (2026-06-01): The previous behaviour fired a global toast
// for EVERY 403 response, which falsely flashed
// "You don't have permission to perform this action." on pages that
// load successfully but whose render hits at least one sub-endpoint
// the current role isn't allowed for (a normal situation in
// multi-role tenant tooling — e.g. a coordinator visiting a clinic
// dashboard that also requests admin-only analytics widgets).
//
// The 403 is still rejected so per-page catch blocks can react and
// surface a *contextual* access-denied state where the failure is
// actually load-blocking. The global blanket toast is removed.
//
// The original error is still rejected so per-page catch blocks may
// still react (e.g. clear state).
let _sessionExpiredHandled = false;
api.interceptors.response.use(
  (response) => response,
  (error) => {
    try {
      const status = error?.response?.status;
      // TREATX-PROVIDER-ACCOUNT-LIFECYCLE-01 — a deactivated provider gets a
      // structured 403 on every operational route. Route them to the public
      // reactivation page rather than showing raw access-denied errors.
      const _detail = error?.response?.data?.detail;
      const _code = (_detail && typeof _detail === 'object' ? _detail.code : undefined)
        || error?.response?.data?.code;
      if (status === 403 && _code === 'CLINIC_DEACTIVATED') {
        if (typeof window !== 'undefined') {
          const h = window.location?.hash || '';
          if (!h.startsWith('#/reactivate')) {
            import('sonner').then(({ toast }) => {
              try { toast.error('This provider account has been deactivated.'); } catch { /* noop */ }
            }).catch(() => { /* noop */ });
            try { localStorage.removeItem('treatx_unified_user'); } catch { /* noop */ }
            try { api.post('/auth/logout').catch(() => { /* noop */ }); } catch { /* noop */ }
            setTimeout(() => { try { window.location.hash = '#/reactivate'; } catch { /* noop */ } }, 250);
          }
        }
        return Promise.reject(error);
      }
      // MTMR-P0-CLOSURE — ONE central handler for the canonical active
      // context failures. The server is the authority; the client must
      // stop Provider-scoped work, drop Provider-sensitive cached state
      // and RE-SELECT a workspace. It must NEVER silently pick another
      // Provider. Handled centrally (no component-by-component hacks):
      // RBACProvider listens for `treatx:context-invalid`.
      if (status === 403 && (
        _code === 'CONTEXT_REVOKED'
        || _code === 'CONTEXT_REQUIRED'
        || _code === 'CONTEXT_NOT_PERMITTED'
      )) {
        if (typeof window !== 'undefined') {
          try {
            window.dispatchEvent(new CustomEvent('treatx:context-invalid', {
              detail: { code: _code, url: error?.config?.url },
            }));
          } catch { /* noop */ }
        }
        return Promise.reject(error);
      }
      if (status === 401) {
        // SEARCH-V2-FINAL-01 (2026-06-17) — public routes must NEVER
        // be hijacked by the session-expiry redirect.  Anonymous
        // visitors and visitors currently viewing a known public
        // surface (Classic Search, Search V2, Landing, public clinic
        // profile) must remain on their page. Only previously-
        // authenticated users on protected surfaces should be sent
        // to /login.
        //
        // FIND-H-08: auth transport is now the HttpOnly `treatx_auth`
        // cookie — JS cannot detect its presence. The prior "did the
        // user hold a token?" heuristic is replaced by the readable
        // `treatx_unified_user` mirror + the readable `treatx_csrf`
        // cookie, either of which indicates "the user WAS logged in
        // recently" without touching the HttpOnly credential.
        const _hash = (typeof window !== 'undefined' && typeof window.location?.hash === 'string')
          ? window.location.hash : '';
        const _hadSession = (() => {
          try {
            // Any of these being present implies the browser was
            // previously authenticated (cookie present at last login).
            if (localStorage.getItem('treatx_unified_user')) return true;
            if (typeof document !== 'undefined' && (document.cookie || '').includes('treatx_csrf=')) return true;
            return false;
          } catch { return false; }
        })();
        const _publicRoutePrefixes = ['#/search-v2', '#/search', '#/clinics', '#/about', '#/privacy', '#/login', '#/'];
        // BUG-4 / P0-PROVIDER-ONBOARDING-RECOVERY-01 — the provider onboarding
        // wizard and the application Status Portal must NEVER be hijacked by
        // the session-expiry redirect. Those visitors are deliberately NOT
        // authenticated users: they hold only an application-bound
        // `status_session` + the readable `treatx_csrf` cookie, which made
        // `_hadSession` true. A background 401 (the periodic
        // `GET /auth/unified/me`, re-fired on every window focus — e.g. when
        // the native file-picker closes) then wiped their mirrors, fired
        // /auth/logout (which deletes the SHARED `treatx_csrf` cookie and so
        // breaks the still-valid portal session) and hash-redirected them to
        // #/login. The provider saw a forced logout the instant they picked a
        // replacement document. These routes own their own not-signed-in
        // state, so a 401 here must be swallowed.
        const _onOnboardingRoute = isSelfOwnedSessionRoute(_hash);
        const _onPublicRoute = _hash === '' || _publicRoutePrefixes.some(
          (p) => _hash === p || _hash === `${p}/` || _hash.startsWith(`${p}?`) || _hash.startsWith(`${p}/`)
        );
        if (!_hadSession || _onPublicRoute || _onOnboardingRoute) {
          // Anonymous user OR explicitly on a public route — swallow
          // the 401 silently.  Caller code can still see the rejected
          // promise and render the appropriate canonical empty state.
          return Promise.reject(error);
        }
        if (typeof window !== 'undefined' && !_sessionExpiredHandled) {
          _sessionExpiredHandled = true;
          // Defer toast import to avoid circular deps at module load.
          import('sonner').then(({ toast }) => {
            try { toast.error('Your session has expired. Please log in again.'); } catch { /* noop */ }
          }).catch(() => { /* noop */ });
          const onLogin = typeof window.location?.hash === 'string' &&
            window.location.hash.startsWith('#/login');
          if (!onLogin) {
            try {
              // FIND-H-08: no localStorage tokens to clear post-cutover.
              // Purge legacy mirrors + the user snapshot so the FE state
              // reflects a logged-out session. The HttpOnly auth cookie
              // itself will be cleared via /api/auth/logout below.
              localStorage.removeItem('treatx_unified_user');
              localStorage.removeItem('treatx_rbac_permissions');
              localStorage.removeItem('treatx_rbac_role_assignments');
              localStorage.removeItem('user');
            } catch { /* noop */ }
            // Fire-and-forget server-side cookie clear.
            try {
              api.post('/auth/logout').catch(() => { /* noop */ });
            } catch { /* noop */ }
            // Small delay so the toast paints before navigation.
            setTimeout(() => {
              try { window.location.hash = '#/login'; } catch { /* noop */ }
              // Reset the latch after navigation so future 401s on a
              // fresh session can re-trigger.
              setTimeout(() => { _sessionExpiredHandled = false; }, 1500);
            }, 250);
          } else {
            _sessionExpiredHandled = false;
          }
        }
      }
      // 403: deliberately no global toast. Per-page handlers may
      // surface a contextual access-denied state when the 403 is
      // load-blocking; transient 403s on background sub-endpoints
      // stay silent.
    } catch { /* never break the response chain */ }
    return Promise.reject(error);
  },
);

// API functions
export const authAPI = {
  // OTP-based auth (legacy)
  requestOTP: (phone) => api.post('/auth/request-otp', { phone }),
  verifyOTP: (phone, otp, user_type) => api.post('/auth/verify-otp', { phone, otp, user_type }),
  
  // Patient email/password auth (new)
  patientRegister: (data) => api.post('/auth/patient/register', data),
  patientLogin: (data) => api.post('/auth/patient/login', data),
  getPatientProfile: () => api.get('/auth/patient/me'),
};

export const patientAPI = {
  createOnboarding: (data) => api.post('/patient/onboarding', data),
  getOnboarding: (userId) => api.get(`/patient/onboarding/${userId}`),
  getSavedClinics: (userId) => api.get(`/saved-clinics/${userId}`),
  saveClinic: (userId, clinicId) => api.post('/saved-clinics', { user_id: userId, clinic_id: clinicId }),
};

export const clinicAPI = {
  createProfile: (data) => api.post('/clinic/profile', data),
  getProfile: (userId) => api.get(`/clinic/profile/${userId}`),
  getClinics: (params) => api.get('/clinics', { params }),
  getClinicById: (clinicId) => api.get(`/clinics/${clinicId}`),
  getAnalytics: (userId) => api.get(`/clinic/analytics/${userId}`),
  uploadDocument: (data) => api.post('/clinic/documents/upload', data),
};

export const aiAPI = {
  matchTreatments: (data) => api.post('/ai/match-treatments', data),
};

// Legacy message API (v1)
export const messageAPI = {
  send: (data) => api.post('/messages', data),
  getMessages: (userId) => api.get(`/messages/${userId}`),
  getConversation: (userId, otherUserId) => api.get(`/messages/conversation/${userId}/${otherUserId}`),
  getUnreadCount: (userId) => api.get(`/messages/unread/${userId}`),
};

// Thread-based messaging API (v2)
export const messagingV2API = {
  // Thread operations
  createThread: (data) => api.post('/v2/threads', data),
  getThread: (threadId) => api.get(`/v2/threads/${threadId}`),
  getUserThreads: (userId, params = {}) => api.get(`/v2/threads`, { params: { user_id: userId, ...params } }),
  getClinicThreads: (clinicId, params = {}) => api.get(`/v2/threads/clinic/${clinicId}`, { params }),
  getAdminThreads: (params = {}) => api.get('/v2/threads/admin', { params }),
  
  // Message operations
  getMessages: (threadId, params = {}) => api.get(`/v2/threads/${threadId}/messages`, { params }),
  sendMessage: (threadId, data) => api.post(`/v2/threads/${threadId}/messages`, data),
  
  // Read tracking
  markRead: (threadId, _userId) => api.post(`/v2/messages/mark-read`, null, { params: { thread_id: threadId } }),
  getUnreadCount: (userId) => api.get(`/v2/messages/unread/${userId}`),
  
  // Thread management
  escalateThread: (threadId, adminId, reason) => api.post(`/v2/threads/${threadId}/escalate`, null, { params: { admin_id: adminId, reason } }),
  resolveThread: (threadId) => api.post(`/v2/threads/${threadId}/resolve`),
  addParticipant: (threadId, data) => api.post(`/v2/threads/${threadId}/participants`, null, { params: data }),
};

export const paymentAPI = {
  // PHASE 2B/B1 — legacy fixed-package createSession removed; patient charges go through /patient/payment/initiate
  getStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

export const contactAPI = {
  submit: (data) => api.post('/contact', data),
};

// ── Corporate Identity & Trust CMS (public, read-only) ──────
// Backend SSOT: LegalEntity + TreatxCorporateCertificate. See
// backend/routes/corporate_identity_routes.py. No auth cookie required;
// endpoints are already allow-listed by the soft-launch gate.
export const corporateIdentityAPI = {
  getDefault: () => api.get('/public/corporate-identity'),
  getEntity: (entityId) => api.get(`/public/corporate-identity/${entityId}`),
  listCertificates: (params = {}) => api.get('/public/corporate-certificates', { params }),
};

// ── Clinic Lead API (backend-driven CRM) ─────────────────────
export const clinicLeadAPI = {
  // Leads
  createLead: (clinicId, data) => api.post(`/clinics/${clinicId}/leads`, data),
  listLeads: (clinicId, params = {}) => api.get(`/clinics/${clinicId}/leads`, { params }),
  updateStatus: (leadId, status, reason) =>
    api.put(`/clinic-leads/${leadId}/status`, { status, reason }),
  // LIFECYCLE-V3.2 — explicit business actions (WON is never a generic status)
  markWon: (leadId, reason, externalReference) =>
    api.post(`/clinic-leads/${leadId}/mark-won`, { reason, external_reference: externalReference || null }),
  reopen: (leadId) => api.post(`/clinic-leads/${leadId}/reopen`),
  statusTransitions: (leadId) => api.get(`/clinic-leads/${leadId}/status-transitions`),

  // Assignment
  assignLead: (leadId, agentId, agentName) =>
    api.put(`/clinic-leads/${leadId}/assign`, {
      assigned_agent_id: agentId,
      assigned_agent_name: agentName || '',
    }),

  // Notes
  addNote: (leadId, content, noteType) =>
    api.post(`/clinic-leads/${leadId}/notes`, { content, note_type: noteType || 'INTERNAL' }),
  listNotes: (leadId) => api.get(`/clinic-leads/${leadId}/notes`),

  // Messages (canonical patient↔provider conversation, same thread the
  // Provider Messages page uses). FASTFIX-CANONICAL-MESSAGING-VISIBILITY-01.
  addMessage: (leadId, content, direction, messageType) =>
    api.post(`/clinic-leads/${leadId}/messages`, {
      content,
      direction: direction || 'OUTBOUND',
      message_type: messageType || 'PATIENT_FACING',
    }),
  listMessages: (leadId) => api.get(`/clinic-leads/${leadId}/messages`),

  // Offers
  createOffer: (leadId, data) => api.post(`/clinic-leads/${leadId}/offers`, data),
  listOffers: (leadId) => api.get(`/clinic-leads/${leadId}/offers`),
  getLatestOffer: (leadId) => api.get(`/clinic-leads/${leadId}/offers/latest`),

  // Invitations
  // PATIENT-INVITE-EMAIL-03 — `resend` retries the e-mail of the SAME pending
  // invitation (the backend never creates a second one).
  sendInvite: (leadId, customMessage, resend = false) =>
    api.post(`/clinic-leads/${leadId}/invite`,
      { custom_message: customMessage || '', resend }),
  validateInvite: (token) => api.get(`/invitations/${token}`),
  acceptInvite: (token, data) => api.post(`/invitations/${token}/accept`, data),

  // Medical files
  uploadMedicalFile: (leadId, file, category, notes) => {
    const form = new FormData();
    form.append('file', file);
    form.append('category', category || 'general');
    form.append('notes', notes || '');
    return api.post(`/clinic-leads/${leadId}/medical-files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  listMedicalFiles: (leadId) => api.get(`/clinic-leads/${leadId}/medical-files`),
};

// ── CRM Quote API (agent-scoped, separate auth) ─────────────
export const crmQuoteAPI = {
  createQuote: (data) => api.post('/crm/quotes', data),
  getLeadQuotes: (leadId) => api.get(`/crm/quotes/lead/${leadId}`),
  downloadPDF: (quoteId) =>
    api.get(`/crm/quotes/${quoteId}/pdf`, { responseType: 'blob' }),
  sendQuote: (quoteId) => api.put(`/crm/quotes/${quoteId}/send`),
};

export default api;